import { dashFor, isDarkColor, needsChartPanel, paintEnvironment, resolvePaint, toSvgPath, } from '@orbit-mind/map-engine';
export const DEFAULT_EXPORT_PALETTE = {
    background: '#ffffff',
    nodeFill: '#ffffff',
    nodeStroke: '#c9cdd6',
    text: '#22252e',
    hierarchyLink: '#c9cdd6',
    crossLink: '#4f6ef7',
};
export const DEFAULT_EXPORT_PADDING = 32;
/**
 * XML-escapes text.
 *
 * The one thing in this file that is not cosmetic. A node containing `<` or `&` produces a file
 * that is not XML at all — no viewer opens it, and the failure looks like corruption rather than
 * like a quoting bug. Attribute values get the quote characters too, because a filename in a
 * `<title>` is user text like any other.
 */
export function escapeXml(value) {
    return value
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&apos;');
}
/** Trims float noise so a golden file does not change because a curve moved by 10⁻¹⁴. */
function n(value) {
    return String(Math.round(value * 100) / 100);
}
/**
 * The rectangle an export covers: the map's own bounds plus room around them.
 *
 * Exported because the PNG path needs the same numbers — a rasteriser that computed its own
 * dimensions would crop or pad differently from the SVG it is rasterising, and the difference
 * would only show at the edges of a file somebody had already sent to someone.
 */
export function exportViewBox(bounds, padding = DEFAULT_EXPORT_PADDING) {
    return {
        x: bounds.x - padding,
        y: bounds.y - padding,
        width: Math.max(1, bounds.width + padding * 2),
        height: Math.max(1, bounds.height + padding * 2),
    };
}
function connectionSvg(link, palette) {
    const stroke = link.color ?? (link.kind === 'hierarchy' ? palette.hierarchyLink : palette.crossLink);
    const dash = dashFor(link.lineType);
    const attributes = [
        `d="${toSvgPath(link.segments)}"`,
        'fill="none"',
        `stroke="${stroke}"`,
        `stroke-width="${n(link.width ?? 1.5)}"`,
        'stroke-linecap="round"',
        'stroke-linejoin="round"',
        dash === null ? null : `stroke-dasharray="${dash.join(' ')}"`,
    ].filter((attribute) => attribute !== null);
    return `    <path ${attributes.join(' ')} />`;
}
/**
 * A node's content — a table, a chart, a drawing, a picture — as SVG elements.
 *
 * The scene already decided every coordinate; this only spells the primitives out. Paints are
 * resolved against the node they sit on: `'ink'` is its text colour, `'surface'` its fill, a
 * series the chart colour for a light or a dark fill, and a wash a gradient defined inline.
 * `idPrefix` keeps those gradients' ids apart when a file holds more than one node.
 */
export function primitivesSvg(primitives, environment, imageHref, idPrefix = 'p') {
    const resolved = typeof environment === 'string'
        ? { ink: environment, surface: '#ffffff', dark: false }
        : environment;
    const out = [];
    let gradients = 0;
    const paint = (value) => {
        if (value === undefined)
            return 'none';
        const colour = resolvePaint(value, resolved);
        if (colour.wash === undefined)
            return colour.color;
        gradients += 1;
        const id = `${idPrefix}-w${String(gradients)}`;
        const end = colour.wash.direction === 'down' ? 'x2="0" y2="1"' : 'x2="1" y2="0"';
        out.push(`<defs><linearGradient id="${id}" x1="0" y1="0" ${end}>` +
            `<stop offset="0" stop-color="${colour.color}" stop-opacity="${n(colour.wash.from)}" />` +
            `<stop offset="1" stop-color="${colour.color}" stop-opacity="${n(colour.wash.to)}" />` +
            `</linearGradient></defs>`);
        return `url(#${id})`;
    };
    const opacity = (value) => value === undefined || value === 1 ? '' : ` opacity="${n(value)}"`;
    for (const primitive of primitives) {
        switch (primitive.kind) {
            case 'rect':
                out.push(`<rect x="${n(primitive.rect.x)}" y="${n(primitive.rect.y)}" width="${n(primitive.rect.width)}" ` +
                    `height="${n(primitive.rect.height)}" rx="${n(primitive.radius ?? 0)}" fill="${paint(primitive.fill)}" ` +
                    `stroke="${paint(primitive.stroke)}" stroke-width="${n(primitive.strokeWidth ?? 0)}"${opacity(primitive.opacity)} />`);
                break;
            case 'path':
                out.push(`<path d="${toSvgPath(primitive.segments)}" fill="${paint(primitive.fill)}" stroke="${paint(primitive.stroke)}" ` +
                    `stroke-width="${n(primitive.strokeWidth ?? 0)}" stroke-linecap="round" stroke-linejoin="round"` +
                    `${primitive.dash === undefined ? '' : ` stroke-dasharray="${primitive.dash.map(n).join(' ')}"`}${opacity(primitive.opacity)} />`);
                break;
            case 'circle':
                out.push(`<circle cx="${n(primitive.cx)}" cy="${n(primitive.cy)}" r="${n(primitive.r)}" fill="${paint(primitive.fill)}" ` +
                    `stroke="${paint(primitive.stroke)}" stroke-width="${n(primitive.strokeWidth ?? 0)}"${opacity(primitive.opacity)} />`);
                break;
            case 'text':
                out.push(`<text x="${n(primitive.x)}" y="${n(primitive.y)}" text-anchor="${primitive.anchor}" ` +
                    `font-size="${n(primitive.size)}" font-weight="${String(primitive.weight)}" ` +
                    `fill="${paint(primitive.fill)}"${opacity(primitive.opacity)}>${escapeXml(primitive.text)}</text>`);
                break;
            case 'image': {
                const href = imageHref?.(primitive.attachmentId);
                if (href === undefined)
                    break;
                out.push(`<image x="${n(primitive.rect.x)}" y="${n(primitive.rect.y)}" width="${n(primitive.rect.width)}" ` +
                    `height="${n(primitive.rect.height)}" preserveAspectRatio="xMidYMid slice" href="${escapeXml(href)}" />`);
                break;
            }
        }
    }
    return out;
}
/** How far the glyph sits inside the node's top-left corner, and how big it is drawn. */
const EMOJI_INSET = 2;
const EMOJI_SIZE = 14;
/** The page's own text colour: what a bare node's words and a structure's marks are drawn in. */
function pageInk(scene, palette, background) {
    // The theme's page says whether it is dark; any other background is measured.
    const canvas = scene.canvas;
    if (canvas?.background === background)
        return canvas.dark ? '#f1f2f5' : palette.text;
    return background !== null && isDarkColor(background) ? '#f1f2f5' : palette.text;
}
function nodeSvg(node, palette, imageHref, page = {
    ink: palette.text,
    background: palette.background,
}) {
    const { rect, textStyle } = node;
    // The scene's own number, not a rule repeated here. A second opinion about how round a box
    // is would be an export that quietly stops matching the screen.
    const radius = node.cornerRadius;
    const lineHeight = textStyle.fontSize * 1.35;
    const paint = [
        `fill="${node.style.fill ?? palette.nodeFill}"`,
        `stroke="${node.style.stroke ?? palette.nodeStroke}"`,
        `stroke-width="${n(node.style.strokeWidth ?? 1)}"`,
    ].join(' ');
    // A rectangle where a rectangle will do, and the scene's own segments where it will not. The
    // canvas draws these same segments, which is what keeps an exported hexagon the hexagon that
    // was on screen.
    // A bare node — a station's name, a column's heading — has no box, only a target to press.
    const box = node.bare === true
        ? `<rect x="${n(rect.x)}" y="${n(rect.y)}" width="${n(rect.width)}" height="${n(rect.height)}" fill="none" />`
        : node.outline === null
            ? `<rect x="${n(rect.x)}" y="${n(rect.y)}" width="${n(rect.width)}" ` +
                `height="${n(rect.height)}" rx="${n(radius)}" ${paint} />`
            : `<path d="${toSvgPath(node.outline)}" ${paint} stroke-linejoin="round" />`;
    // Text sits in its own band when the node also holds content; otherwise in the whole box.
    const textBox = node.textBox ?? rect;
    const centreX = textBox.x + textBox.width / 2;
    const firstY = textBox.y + textBox.height / 2 - ((node.lines.length - 1) * lineHeight) / 2;
    // Real `<text>`, never paths. Acceptance criterion 8 asks for text that is still text, and a
    // reader who cannot select a word out of a map they exported has been handed a picture of it.
    const spans = node.lines
        .map((line, index) => `<tspan x="${n(centreX)}"${index === 0 ? '' : ` dy="${n(lineHeight)}"`}>${escapeXml(line)}</tspan>`)
        .join('');
    const textColour = node.style.textColor ?? (node.bare === true ? page.ink : palette.text);
    const text = node.lines.length === 0
        ? ''
        : `\n      <text x="${n(centreX)}" y="${n(firstY)}" text-anchor="middle" dominant-baseline="central" ` +
            `font-family="${escapeXml(textStyle.fontFamily)}" font-size="${n(textStyle.fontSize)}" ` +
            `font-weight="${String(textStyle.fontWeight)}"${textStyle.italic ? ' font-style="italic"' : ''} ` +
            `fill="${textColour}">${spans}</text>`;
    const glyph = node.glyph === undefined || node.glyph.length === 0
        ? ''
        : `\n      <g>${primitivesSvg(node.glyph, node.bare === true
            ? paintEnvironment(page.ink, page.background)
            : paintEnvironment(textColour, node.style.fill ?? palette.nodeFill), undefined, `g${node.id.replace(/[^A-Za-z0-9_-]/g, '')}`).join('')}</g>`;
    /*
     * The node's glyph, drawn where the canvas draws it.
     *
     * Above the top-left corner, mirroring the attachment badge on the right. It is `<text>` like
     * everything else here, so it stays a character rather than becoming a picture of one, and an
     * SVG viewer renders it with whatever font the reader has.
     *
     * The PDF export skips it deliberately: that file is written against the standard Latin-1
     * fonts, so an emoji there becomes a question mark — and the export already counts and reports
     * the characters it could not represent.
     */
    const emoji = node.emoji === undefined
        ? ''
        : `\n      <text x="${n(rect.x + EMOJI_INSET)}" y="${n(rect.y + EMOJI_INSET)}" ` +
            `text-anchor="middle" dominant-baseline="central" font-size="${n(EMOJI_SIZE)}">` +
            `${escapeXml(node.emoji)}</text>`;
    const ink = node.style.textColor ?? palette.text;
    const fill = node.style.fill ?? palette.nodeFill;
    // A chart on a saturated card sits on a neutral panel, drawn in the page's own ink — as on
    // screen, so the chart colours read as they were chosen to.
    const panel = node.content?.kind === 'chart' && needsChartPanel(fill) ? node.content.rect : null;
    const environment = panel === null ? paintEnvironment(ink, fill) : paintEnvironment(palette.text, palette.nodeFill);
    const prefix = `n${node.id.replace(/[^A-Za-z0-9_-]/g, '')}`;
    const extras = [
        ...(panel === null
            ? []
            : [
                `<rect x="${n(panel.x - 5)}" y="${n(panel.y - 5)}" width="${n(panel.width + 10)}" ` +
                    `height="${n(panel.height + 10)}" rx="8" fill="${palette.nodeFill}" ` +
                    `stroke="${palette.nodeStroke}" stroke-width="0.75" />`,
            ]),
        ...primitivesSvg(node.content?.primitives ?? [], environment, imageHref, prefix),
        ...primitivesSvg(node.link?.primitives ?? [], environment, undefined, `${prefix}l`),
    ];
    const content = extras.length === 0
        ? ''
        : `\n      <g font-family="${escapeXml(textStyle.fontFamily)}">${extras.join('')}</g>`;
    return `    <g>\n      ${box}${glyph}${content}${emoji}${text}\n    </g>`;
}
/**
 * The whole scene as one standalone SVG document.
 *
 * Standalone means it opens on its own: an XML declaration, the namespace, and explicit
 * dimensions. A fragment that only renders inside the application that made it is not an export.
 */
export function sceneToSvg(scene, options = {}) {
    const palette = { ...DEFAULT_EXPORT_PALETTE, ...options.palette };
    const padding = options.padding ?? DEFAULT_EXPORT_PADDING;
    const view = exportViewBox(scene.bounds, padding);
    const backgroundColour = options.background === null
        ? null
        : (options.background ?? scene.canvas?.background ?? palette.background);
    const background = backgroundColour === null
        ? ''
        : `\n  <rect x="${n(view.x)}" y="${n(view.y)}" width="${n(view.width)}" height="${n(view.height)}" ` +
            `fill="${backgroundColour}" />`;
    const page = {
        ink: pageInk(scene, palette, backgroundColour),
        background: backgroundColour ?? palette.background,
    };
    // A structure's picture — lines, lanes, a road — under everything else.
    const underlay = scene.underlay === undefined || scene.underlay.length === 0
        ? ''
        : `\n  <g>${primitivesSvg(scene.underlay, paintEnvironment(page.ink, page.background), undefined, 'u').join('')}</g>`;
    const title = options.title === undefined ? '' : `\n  <title>${escapeXml(options.title)}</title>`;
    // Connections first so a line never crosses over the box it arrives at.
    const connections = scene.connections.length === 0
        ? ''
        : `\n  <g>\n${scene.connections.map((link) => connectionSvg(link, palette)).join('\n')}\n  </g>`;
    const nodes = scene.nodes.length === 0
        ? ''
        : `\n  <g>\n${scene.nodes.map((node) => nodeSvg(node, palette, options.imageHref, page)).join('\n')}\n  </g>`;
    return (`<?xml version="1.0" encoding="UTF-8"?>\n` +
        `<svg xmlns="http://www.w3.org/2000/svg" ` +
        `width="${n(view.width)}" height="${n(view.height)}" ` +
        `viewBox="${n(view.x)} ${n(view.y)} ${n(view.width)} ${n(view.height)}">` +
        `${title}${background}${underlay}${connections}${nodes}\n</svg>\n`);
}
/** What a browser download or a Worker response needs to call this file what it is. */
export const SVG_MIME_TYPE = 'image/svg+xml';
/** A filename from a map's title, safe on every filesystem this ships to. */
export function exportFilename(title, extension) {
    const cleaned = title
        .trim()
        .replaceAll(/[\\/:*?"<>|]/g, '-')
        .replaceAll(/\s+/g, ' ')
        .slice(0, 80);
    return `${cleaned === '' ? 'map' : cleaned}.${extension}`;
}
//# sourceMappingURL=svg.js.map